import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useCmsContent } from '../hooks/useCmsData';

export function BeforeAfterSlider() {
  const { items: cmsBeforeAfter } = useCmsContent('before_after');
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  // Pick first valid CMS before/after item or null
  const activeItem = cmsBeforeAfter && cmsBeforeAfter.length > 0 ? cmsBeforeAfter[0] : null;

  // Extract valid image URLs from CMS arrays or strings
  const getImageUrl = (img) => {
    if (Array.isArray(img) && img.length > 0) return img[0];
    if (typeof img === 'string') return img;
    return null;
  };

  const beforeImg = activeItem ? getImageUrl(activeItem.before_image) : null;
  const afterImg = activeItem ? getImageUrl(activeItem.after_image) : null;

  const formatCdnUrl = (url) => {
    if (!url) return '';
    if (url.includes('/cdn-cgi/image/')) return url;
    if (url.startsWith('https://assets.auxodev.dk/')) {
      return `https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/${url.replace('https://assets.auxodev.dk/', '')}`;
    }
    return url;
  };

  const handleMove = useCallback((clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    let percentage = (x / rect.width) * 100;
    if (percentage < 0) percentage = 0;
    if (percentage > 100) percentage = 100;
    setSliderPosition(percentage);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    if (e.touches && e.touches[0]) {
      handleMove(e.touches[0].clientX);
    }
  }, [isDragging, handleMove]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  }, [isDragging, handleMove]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove]);

  // Gracefully return null if no valid image pair exists
  if (!beforeImg || !afterImg) {
    return null;
  }

  return (
    <section className="py-12 bg-white border-y border-sand-border" id="bagside-forside">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        
        {/* Header bar */}
        <div className="flex items-center justify-between gap-3 mb-6 pb-3 border-b border-sand-border">
          <div className="flex items-center gap-2.5">
            <span className="w-3 h-3 rounded-full bg-[#2193BB] inline-block" />
            <h3 className="text-xl sm:text-2xl font-extrabold text-forest tracking-tight font-display">
              Bagside & Forside Resultat
            </h3>
          </div>
        </div>

        {/* Interactive Slider View */}
        <div className="max-w-4xl mx-auto">
          <div 
            ref={containerRef}
            className="relative w-full aspect-4/3 sm:aspect-16/10 rounded-2xl overflow-hidden shadow-xl select-none cursor-ew-resize border-2 border-sand-border bg-sand-light touch-none"
            onMouseDown={(e) => {
              setIsDragging(true);
              handleMove(e.clientX);
            }}
            onTouchStart={(e) => {
              setIsDragging(true);
              if (e.touches && e.touches[0]) handleMove(e.touches[0].clientX);
            }}
          >
            {/* Forside (After) Image */}
            <img 
              src={formatCdnUrl(afterImg)}
              alt="Forside"
              className="absolute inset-0 w-full h-full object-cover"
              draggable={false}
            />
            <div className="absolute top-4 right-4 z-10 bg-forest/90 text-white font-bold text-xs px-3.5 py-1.5 rounded-lg shadow-md uppercase tracking-wider backdrop-blur-xs flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block"></span>
              <span>Forside</span>
            </div>

            {/* Bagside (Before) Image */}
            <div 
              className="absolute inset-0 overflow-hidden"
              style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
            >
              <img 
                src={formatCdnUrl(beforeImg)}
                alt="Bagside"
                className="absolute inset-0 w-full h-full object-cover"
                draggable={false}
              />
              <div className="absolute top-4 left-4 z-10 bg-charcoal/90 text-white font-bold text-xs px-3.5 py-1.5 rounded-lg shadow-md uppercase tracking-wider backdrop-blur-xs flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-white inline-block"></span>
                <span>Bagside</span>
              </div>
            </div>

            {/* Divider line & handle */}
            <div 
              className="absolute top-0 bottom-0 z-20 w-1 bg-white shadow-2xl pointer-events-none"
              style={{ left: `${sliderPosition}%` }}
            >
              <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 bg-white text-forest rounded-full shadow-2xl flex items-center justify-center border-2 border-forest transition-transform">
                <i className="fa-solid fa-arrows-left-right text-xs" aria-hidden="true" />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center mt-3 px-1 text-xs text-muted font-medium">
            <span className="flex items-center gap-1"><i className="fa-solid fa-hand-pointer text-[#2193BB]" /> Træk eller klik for at sammenligne</span>
          </div>
        </div>

      </div>
    </section>
  );
}
